import React from 'react'
import Pill from 'components/Pill'
import { ChevronRightIcon, FilterSearchBarIcon } from 'components/icons'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { DestinationFilter, PropertiesOrderByClause } from 'gql/graphql'
import classNames from 'classnames'
import PropertiesSortSelect from 'components/property/PropertiesSortSelect'
import { MobileScreenWidths, useScreenWidth } from 'data/hooks/useScreenWidth'

interface DestinationFiltersSectionProps {
  filters: Pick<DestinationFilter, 'id' | 'value'>[]
  activeFilters: number[]
  setActiveFilters: (filters: number[]) => void
  setOrderBy: (sort: PropertiesOrderByClause[]) => void
  className?: {
    wrapper?: string
    container?: string
  }
}

const DestinationFiltersSection = ({
  filters = [],
  activeFilters = [],
  setActiveFilters = () => { },
  setOrderBy,
  className,
}: DestinationFiltersSectionProps) => {
  const { isMobile } = useScreenWidth(MobileScreenWidths.LG)

  const onPillClicked = (id: number) => {
    if (activeFilters.includes(id)) {
      setActiveFilters(activeFilters.filter(item => item !== id))
    } else {
      setActiveFilters([...activeFilters, id])
    }
  }

  const navigationNextRef = React.useRef(null)

  return (
    <div className={classNames(
      'flex flex-col justify-between gap-20 lg:items-center lg:flex-row mb-20 lg:mb-50 lg:px-20 mt-30 lg:mt-0',
      className?.container,
    )}>
      {(filters?.length > 0 && !isMobile)
        ? <>
          <p className="flex items-center gap-10 px-20 lg:px-0 text-grey-750">
            <FilterSearchBarIcon className="text-primary-fresh" />Filters:
          </p>

          <div className="flex items-center justify-between w-full min-w-0">
            <div className="relative flex min-w-0">
              <Swiper
                modules={[Navigation]}
                className="pl-20"
                slidesPerView={'auto'}
                freeMode={true}
                navigation={{
                  nextEl: navigationNextRef.current,
                }}
                onSwiper={(swiper: SwiperClass) => {
                  setTimeout(() => {
                    // Timeout until prevEl & nextEl refs are defined
                    if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
                      swiper.params.navigation.nextEl = navigationNextRef.current
                    }

                    // Re-initialise navigation
                    swiper.navigation.destroy()
                    swiper.navigation.init()
                    swiper.navigation.update()
                  })
                }}
                observer={true}
              >
                {
                  filters?.map((tag, index) => (
                    <SwiperSlide className="mr-18 w-max" key={index}>
                      <Pill
                        text={tag?.value}
                        action={() => onPillClicked(parseInt(tag.id))}
                        variant="public"
                        isActive={activeFilters.includes(parseInt(tag.id))}
                      />
                    </SwiperSlide>
                  ))
                }
              </Swiper>
              <div className="absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-l from-transparent to-white" />
              <div className="absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-r from-transparent to-white" />
            </div>

            <div ref={navigationNextRef} className="flex items-center justify-center min-w-[24px] max-w-[24px] h-24 border cursor-pointer rounded-full border-grey-400 mr-20 lg:ml-0">
              <ChevronRightIcon className="ml-[1px]" />
            </div>
          </div>
        </>
        : null
      }

      <PropertiesSortSelect
        setOrderBy={setOrderBy}
        className={{
          container: 'mx-20 lg:mx-0',
        }}
      />
    </div>
  )
}

export default DestinationFiltersSection
